import { useMemo } from "react";
import { faBolt } from "@fortawesome/pro-solid-svg-icons";
import { InteractionMessageType, Sentiment } from "@edgetier/watchtower-types";
import { SentimentIcon } from "../../../sentiment-icon";
import { Group } from "../../../group";
import { Icon } from "../../../icon";
import { Text } from "../../../text";

/**
 * Displays the message author name with optional sentiment icons indicating message emotions.
 * @param {Array<Sentiment | InteractionMessageType.System>} props.messageIcons - Optional array of sentiment or system icon types to display.
 * @param {string} props.author - The name of the message author.
 * @returns {JSX.Element} The MessageAuthor component.
 */
const MessageAuthor = ({
    author,
    messageIcons,
}: {
    messageIcons?: Array<Sentiment | InteractionMessageType.System>;
    author: string;
}) => {
    const iconOptions = useMemo(() => {
        return (
            messageIcons?.map((icon: Sentiment | InteractionMessageType.System) => {
                switch (icon) {
                    case Sentiment.Frustration:
                        return {
                            component: <SentimentIcon.Frustration key={`sentiment-${icon}`} />,
                            color: "var(--mantine-color-text-error-default)",
                        };
                    case Sentiment.Confusion:
                        return {
                            component: <SentimentIcon.Confusion key={`sentiment-${icon}`} />,
                            color: "var(--mantine-color-text-warning-default)",
                        };
                    case Sentiment.Gratitude:
                        return {
                            component: <SentimentIcon.Gratitude key={`sentiment-${icon}`} />,
                            color: "var(--mantine-color-text-primary-default)",
                        };
                    case Sentiment.Praise:
                        return {
                            component: <SentimentIcon.Praise key={`sentiment-${icon}`} />,
                            color: "var(--mantine-color-text-success-default)",
                        };
                    case Sentiment.Empathy:
                        return {
                            component: <SentimentIcon.Empathy key={`sentiment-${icon}`} />,
                            color: "var(--mantine-color-text-violet-default)",
                        };
                    case InteractionMessageType.System:
                        return {
                            component: (
                                <Icon
                                    icon={faBolt}
                                    key={`sentiment-${icon}`}
                                    size="xs"
                                    color="var(--mantine-color-neutral-6)"
                                />
                            ),
                            color: "dimmed",
                        };
                }
            }) ?? []
        );
    }, [messageIcons]);

    const color = useMemo(() => {
        if (typeof iconOptions === "undefined" || iconOptions.length === 0) {
            return "dimmed";
        }
        return iconOptions[iconOptions.length - 1].color;
    }, [iconOptions]);

    return (
        <Group gap="xs" c={color}>
            {iconOptions.map((item) => item.component)}
            <Text variant="medium" size="xs" data-testid="chat-author-name">
                {author}
            </Text>
        </Group>
    );
};
/**
 * Loading skeleton placeholder for the MessageAuthor component.
 * @returns {JSX.Element} The MessageAuthorSkeleton component.
 */
const MessageAuthorSkeleton = () => {
    return <Text.Skeleton size="xs" width={150} />;
};
MessageAuthor.Skeleton = MessageAuthorSkeleton;

export { MessageAuthor };
